import React, { useState } from 'react';
import { useRecordContext, useRefresh, useNotify } from 'react-admin';
import SendIcon from '@mui/icons-material/Send';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';

import IconButton from '../../common/buttons/IconButton';
import { sendInvoicePeppol } from '../../../services/api';

const PeppolStatus = {
	TRANSPORT_ACK_RECEIVED: "TRANSPORT_ACK_RECEIVED",
	INVOICE_SENT: "INVOICE_SENT"
};

const SendPeppolButton = () => {
	const record = useRecordContext();
	const refresh = useRefresh();
	const notify = useNotify();
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [result, setResult] = useState(null);

	const handleSend = async () => {
		setLoading(true);
		setError(null);
		try {
			const data = await sendInvoicePeppol(record?.id);

			if (!data.success && data.message) {
				setError(data.message);
				notify(data.message, { type: 'error' });
			} else if (data.id) {
				setResult(data);
				notify('Invoice sent via PEPPOL successfully', { type: 'success' });
				refresh();
				// Close dialog after a short delay
				setTimeout(() => {
					setOpen(false);
					setResult(null);
				}, 2000);
			}
		} catch (err) {
			const errorMessage = err.message || 'Error sending to PEPPOL';
			setError(errorMessage);
			notify('Error sending to PEPPOL', { type: 'error' });
		} finally {
			setLoading(false);
		}
	};

	const handleOpen = async () => {
		setOpen(true);
		await handleSend();
	};

	const handleClose = () => {
		setOpen(false);
		setError(null);
		setResult(null);
	};

	if (!record) {
		return null;
	}

	// Show if invoice has been sent (let API handle PEPPOL validation)
	// Only hide if already sent to PEPPOL
	const alreadySentToPeppol = [PeppolStatus.INVOICE_SENT, PeppolStatus.TRANSPORT_ACK_RECEIVED].includes(record.peppol_status);
	const shouldShow = record.sent_at && !alreadySentToPeppol;

	if (!shouldShow) {
		return null;
	}

	return (
		<>
			<IconButton icon={<SendIcon />} onClick={handleOpen}>Send to PEPPOL</IconButton>

			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
				<DialogTitle>Send Invoice via PEPPOL</DialogTitle>
				<DialogContent sx={{ minHeight: '200px' }}>
					{loading ? (
						<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
							<Box textAlign="center">
								<CircularProgress sx={{ mb: 2 }} />
								<div>Sending to PEPPOL network...</div>
							</Box>
						</Box>
					) : error ? (
						<Alert severity="error">{error}</Alert>
					) : result ? (
						<Box>
							<Alert severity="success" sx={{ mb: 2 }}>
								Invoice sent via PEPPOL successfully
							</Alert>
							<Box sx={{ mt: 2 }}>
								<div><strong>Invoice:</strong> {result.number}</div>
								{result.peppol_status && (
									<div><strong>PEPPOL Status:</strong> {result.peppol_status}</div>
								)}
							</Box>
						</Box>
					) : (
						<Alert severity="info">
							Do you want to send invoice {record.number} via the PEPPOL network?
						</Alert>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary">
						{result ? 'Close' : 'Cancel'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default SendPeppolButton;
